import React, { useRef, useState } from "react";
import { ConfirmationBarDialog } from "../dialog/confirmation-bar/confirmation-bar-dialog";
import { MapContainer, TileLayer } from "react-leaflet";
import { LatLngTuple, Map } from "leaflet";
import "leaflet/dist/leaflet.css";

import "./bottom-photo-maps-dialog.css";
import { useAppContext } from "../../hooks/useAppContext";
import { useMapStyles } from "./hooks/use-map-styles";
import { PlaceholderImage } from "../placeholder";
import { ActiveBox } from "../active-box";

import { labels } from "./labels";
import { useLabels } from "../../hooks/useLabels";
import { SearchBox } from "@mapbox/search-js-react";

/** These values can be found in osm-tile-server/config.json */
const maxSize = 8192;
const maxScale = 9;

const defaultMapPhotoSourceData: Required<eXpress.client.IMapPhotoSourceData> = {
	location: "Brussels",
	mapStyle: "basic",
	lng: 4.3185893,
	lat: 50.8493131,
	zoom: 12,
	url: "",
	height: 0,
	width: 0,
};

export const BottomPhotoMapsDialog = ({
	initialData,
	onOk,
	onCancel,
}: {
	initialData: eXpress.client.IMapPhotoSourceData | undefined;
	onOk: ({ lat, lng, zoom }: eXpress.client.IMapPhotoSourceData) => void;
	onCancel: () => void;
}) => {
	initialData = {
		...defaultMapPhotoSourceData,
		...initialData,
	};
	const mapRef = useRef<Map>(null);
	const [selectedAddress, setSelectedAddress] = useState<string>("");
	const { context } = useAppContext();
	const center = {
		lat: initialData.lat ?? 0,
		lng: initialData.lng ?? 0,
	};

	const [[mapStyleTitle]] = useLabels([labels.mapStyleTitle]);

	const [selectedStyle, setSelectedStyle] = useState<string>(initialData.mapStyle ?? "basic");
	const { data: mapStyles = [] } = useMapStyles();

	// later we should set set this from the default location configured on a design/zone
	// this is used to sort the results on proximity
	// this way results will be sorted closest->furthest, based on the default map location and should give good results for different countries
	const proximity =
		initialData.lat && initialData.lng ? `${initialData.lng},${initialData.lat}` : undefined;
	return (
		<ConfirmationBarDialog
			onCancel={onCancel}
			onOk={() => {
				const map = mapRef.current;
				if (!map) {
					onCancel();
					return;
				}
				const { lat, lng } = map.getCenter();
				const zoom = map.getZoom();

				const zoneSize = map.getSize();
				const scaleFactor = Math.min(
					maxScale,
					Math.floor(Math.max(maxSize / zoneSize.x, maxSize / zoneSize.y)),
				);

				// width/height is limited by 'maxSize' prop in osm-tile-server/config.json, currently set to 4096
				// to guarantee the same level of detail on the static image as in the interactive map, we need to get an image with the same zoomlevel
				// we can use the '@6x' prop at the end of the url, this will render every tile in the image x times bigger, so the resulting image is x times bigger
				// the max value we can use after @ is 9 (currently 6, defined in config.json), to be checked if this results in a high enough quality image
				onOk({
					...initialData,
					lat,
					lng,
					zoom,
					mapStyle: selectedStyle,
					url: `${context.osmTileServerUrl}/styles/${selectedStyle}/static/${
						map.getCenter().lng
					},${map.getCenter().lat},${map.getZoom()}/${zoneSize.x}x${
						zoneSize.y
					}@${scaleFactor}x.png`,
					width: zoneSize.x * scaleFactor,
					height: zoneSize.y * scaleFactor,
				});
			}}
		>
			<div className="bottom-photo-maps-dialog__dialog-content">
				<MapContainer
					attributionControl={false}
					className="bottom-photo-maps-dialog__map-container"
					center={center}
					zoom={initialData.zoom}
					scrollWheelZoom={true}
					ref={mapRef}
				>
					<TileLayer
						url={`${context.osmTileServerUrl}/styles/${selectedStyle}/{z}/{x}/{y}.png`}
					/>
				</MapContainer>
				<SearchBox
					accessToken={context.osmMapboxAccessToken}
					options={{
						language: context.language, // IETF language tag
						types: "country, region, place, postcode, street, address, locality",
						...(initialData.lng && initialData.lat && { proximity }),
						limit: 10,
					}}
					onRetrieve={(result) => {
						const clicked = result.features[0];
						setSelectedAddress(clicked.properties.full_address);

						const bounds = clicked.properties.bbox;
						if (bounds) {
							// if there are bounds we can use those to go to the correct location, zoom will automatically change to bounding box
							const northWest: LatLngTuple = [bounds[1], bounds[0]];
							const southEast: LatLngTuple = [bounds[3], bounds[2]];

							mapRef.current?.fitBounds([northWest, southEast], { animate: false });
						} else {
							// if no bounds then go to exact location with zoomlevel (if no bounds are available it is usually a street or address)
							mapRef.current?.setView(
								[clicked.geometry.coordinates[1], clicked.geometry.coordinates[0]],
								17,
							);
						}
					}}
					value={selectedAddress}
					theme={{
						cssText: `.Results 
								{
									position: fixed;
									display: flex;
									flex-direction: column;
									height: -webkit-fill-available;
									margin-bottom: 1rem;
								}
								.ResultsList
								{
									display: flex;
									flex-direction: column;
									overflow-y: scroll;
								}
								.ResultsAttribution {
								display: none;
								}
        `,
					}}
				/>
				<div className="bottom-photo-maps-dialog__map-styles-container">
					<div>{mapStyleTitle}</div>
					<div className="bottom-photo-maps-dialog__map-styles-list">
						{mapStyles.map((mapStyle) => (
							<ActiveBox
								key={mapStyle.id}
								isActive={mapStyle.id === selectedStyle}
								className="bottom-photo-maps-dialog__map-style"
							>
								<PlaceholderImage
									className="bottom-photo-maps-dialog__map-style-image"
									src={mapStyle.url}
									onClick={() => {
										setSelectedStyle(mapStyle.id);
									}}
								/>
								<div className="bottom-photo-maps-dialog__map-style-name">
									{mapStyle.name}
								</div>
							</ActiveBox>
						))}
					</div>
				</div>
			</div>
		</ConfirmationBarDialog>
	);
};
